import { useId } from 'react';
import {
  STATUS_SWATCH,
  findLevel,
  projectWideWarranties,
  type Dwelling,
  type DwellingStatus,
  type Level,
  type Unit,
  type Warranties,
} from '@alrimal/contracts';
import { useLocale } from '../LocaleContext.tsx';
import type { MessageKey } from '../i18n.ts';

/**
 * The unit panel (§7).
 *
 * It opens **over** the model, not instead of it, and it leads with the
 * dwelling selector. That order is the whole point: the villa product is two
 * separate homes in one shell, each with its own entrance, its own status and
 * potentially its own price. An interface that opened on a floor plan would be
 * telling the buyer something untrue before it told them anything else.
 */

export interface UnitPanelProps {
  unit: Unit;
  dwelling: Dwelling | null;
  levelCode: string | null;
  warranties: Warranties;
  onSelectDwelling(dwellingId: string): void;
  onSelectLevel(levelCode: string | null): void;
  onRequestPrice(): void;
  /** Present only when the unit actually has a panorama set (§13's slot). */
  onOpenPanorama?: () => void;
  onClose(): void;
  containerRef?: React.Ref<HTMLElement>;
}

export function UnitPanel({
  unit,
  dwelling,
  levelCode,
  warranties,
  onSelectDwelling,
  onSelectLevel,
  onRequestPrice,
  onOpenPanorama,
  onClose,
  containerRef,
}: UnitPanelProps) {
  const { t, price } = useLocale();
  const titleId = useId();
  const level =
    dwelling && levelCode ? (findLevel(dwelling, levelCode) ?? null) : null;

  return (
    <section className="panel" aria-labelledby={titleId} ref={containerRef}>
      <header className="panel__header">
        <div className="panel__heading">
          <p className="panel__eyebrow">{unit.id}</p>
          <h2 className="panel__title" id={titleId}>
            {dwelling ? dwelling.labelAr : productLabel(unit)}
          </h2>
        </div>
        <button
          type="button"
          className="panel__close"
          onClick={onClose}
          aria-label={t('action.close')}
        >
          ✕
        </button>
      </header>

      <div className="panel__body">
        {unit.dwellings.length > 1 ? (
          <DwellingSelector
            unit={unit}
            selectedId={dwelling?.id ?? null}
            onSelect={onSelectDwelling}
          />
        ) : null}

        {dwelling ? (
          <>
            <LevelTabs
              dwelling={dwelling}
              selected={levelCode}
              onSelect={onSelectLevel}
            />
            <Facts unit={unit} level={level} />
            {level ? <RoomList level={level} /> : null}
          </>
        ) : null}

        {unit.panoramaSet !== null && onOpenPanorama ? (
          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={onOpenPanorama}
          >
            {t('action.panorama')}
          </button>
        ) : null}

        <ProjectWarranties warranties={warranties} />
      </div>

      <footer className="panel__footer">
        {dwelling ? (
          <p className="dwelling__price">
            {dwelling.price === null ? (
              t('dwelling.priceOnRequest')
            ) : (
              <>
                {price(dwelling.price)}
                {dwelling.priceIsDemo ? (
                  <span className="demo-flag">{t('price.demo')}</span>
                ) : null}
              </>
            )}
          </p>
        ) : null}
        <button
          type="button"
          className="btn btn--primary btn--block"
          onClick={onRequestPrice}
        >
          {t('action.requestPrice')}
        </button>
      </footer>
    </section>
  );
}

// ---------------------------------------------------------------------------

function DwellingSelector({
  unit,
  selectedId,
  onSelect,
}: {
  unit: Unit;
  selectedId: string | null;
  onSelect(id: string): void;
}) {
  const { t, price } = useLocale();

  return (
    <div className="dwellings">
      <h3 className="section__title">{t('dwelling.title')}</h3>
      <p className="dwellings__hint">{t('dwelling.hint')}</p>
      <ul className="dwellings__list">
        {unit.dwellings.map((dwelling) => (
          <li key={dwelling.id}>
            <button
              type="button"
              className="dwelling"
              aria-pressed={dwelling.id === selectedId}
              onClick={() => onSelect(dwelling.id)}
            >
              <StatusPip status={dwelling.status} />
              <span>
                <span className="dwelling__label">{dwelling.labelAr}</span>
                <span className="dwelling__meta">
                  {' '}
                  {dwelling.levels.length === 1
                    ? dwelling.levels[0]?.nameAr
                    : dwelling.levels.map((l) => l.nameAr).join(' · ')}
                  {dwelling.separateEntrance
                    ? ` · ${t('dwelling.separateEntrance')}`
                    : ''}
                </span>
              </span>
              <span className="dwelling__price">
                {dwelling.price === null ? (
                  t('dwelling.priceOnRequest')
                ) : (
                  <>
                    {price(dwelling.price)}
                    {dwelling.priceIsDemo ? (
                      <span className="demo-flag">{t('price.demo')}</span>
                    ) : null}
                  </>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LevelTabs({
  dwelling,
  selected,
  onSelect,
}: {
  dwelling: Dwelling;
  selected: string | null;
  onSelect(code: string | null): void;
}) {
  const { t } = useLocale();

  return (
    <div className="levels">
      <div
        className="levels__tablist"
        role="tablist"
        aria-label={t('level.title')}
      >
        <button
          type="button"
          role="tab"
          className="level-tab"
          aria-selected={selected === null}
          onClick={() => onSelect(null)}
        >
          {t('level.all')}
        </button>
        {dwelling.levels.map((level) => (
          <button
            key={level.code}
            type="button"
            role="tab"
            className="level-tab"
            aria-selected={selected === level.code}
            onClick={() => onSelect(level.code)}
          >
            {level.nameAr}
          </button>
        ))}
      </div>
    </div>
  );
}

function Facts({ unit, level }: { unit: Unit; level: Level | null }) {
  const { t } = useLocale();

  return (
    <dl className="facts">
      {unit.area !== undefined ? (
        <Fact
          label={t('fact.area')}
          value={
            <>
              <span className="num">{unit.area}</span>{' '}
              <small>{t('fact.squareMetres')}</small>
              {unit.areaBasis === 'TBC' ? (
                <>
                  {' '}
                  <small>({t('fact.areaBasisTbc')})</small>
                </>
              ) : null}
            </>
          }
        />
      ) : null}
      <Fact
        label={t('fact.street')}
        value={
          <>
            <span className="num">{unit.streetWidth}</span>{' '}
            <small>{t('fact.metres')}</small>
          </>
        }
      />
      <Fact label={t('fact.orientation')} value={unit.orientationAr} />
      <Fact
        label={t('fact.bathrooms')}
        value={
          level?.bathrooms == null ? (
            t('fact.unknown')
          ) : (
            <span className="num">{level.bathrooms}</span>
          )
        }
      />
    </dl>
  );
}

function Fact({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="fact">
      <dt className="fact__label">{label}</dt>
      <dd className="fact__value">{value}</dd>
    </div>
  );
}

function RoomList({ level }: { level: Level }) {
  const { t } = useLocale();
  return (
    <div className="rooms">
      <h3 className="rooms__title">{t('rooms.title')}</h3>
      <ul className="rooms__list">
        {level.roomsAr.map((room) => (
          <li className="rooms__item" key={room}>
            {room}
          </li>
        ))}
        {level.area != null ? (
          <li className="rooms__item rooms__item--area">
            <span className="num">{level.area}</span> {t('fact.squareMetres')}
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function ProjectWarranties({ warranties }: { warranties: Warranties }) {
  const { t, years } = useLocale();
  const groups = projectWideWarranties(warranties);
  if (groups.length === 0) return null;

  return (
    <div className="section">
      <h3 className="section__title">{t('warranties.title')}</h3>
      <ul className="rooms__list">
        {groups.flatMap(({ key, items }) =>
          items.map((item) => (
            <li className="rooms__item" key={`${key}-${item.labelAr}`}>
              {item.labelAr} — <span className="num">{years(item.years)}</span>
              {item.noteAr ? ` · ${item.noteAr}` : ''}
            </li>
          )),
        )}
      </ul>
    </div>
  );
}

export function StatusPip({ status }: { status: DwellingStatus }) {
  const { t } = useLocale();
  return (
    <span className="status">
      <span
        className="status__pip"
        style={{ '--pip': STATUS_SWATCH[status] } as React.CSSProperties}
        aria-hidden="true"
      />
      <span className="visually-hidden">
        {t(`status.${status}` as MessageKey)}
      </span>
    </span>
  );
}

function productLabel(unit: Unit): string {
  return unit.dwellings[0]?.labelAr ?? unit.id;
}
